import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  TrendingUp,
  Target,
  Timer,
  Trophy,
  MessageSquareWarning,
  Globe,
  Megaphone,
  Radio,
  Funnel,
  RotateCcw,
  AlertTriangle,
  Loader2,
} from "lucide-react";

const SOURCE_ICONS = { globe: Globe, megaphone: Megaphone, radio: Radio };
const SOURCE_TINTS = {
  chatbot: { bar: "bg-emerald-500/70", text: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/25" },
  "hear-ashley": { bar: "bg-[#FFB000]", text: "text-[#FFB000]", bg: "bg-[#FFB000]/10", border: "border-[#FFB000]/30" },
  outbound: { bar: "bg-sky-400", text: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/25" },
};

const formatMinutes = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m.toFixed(1)}m`;
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return `${h}h ${mm}m`;
};

const Kpi = ({ icon: Icon, label, value, hint, testid }) => (
  <div
    data-testid={testid}
    className="bg-[#121214] border border-white/10 rounded-xl p-5 hover:border-[#FFB000]/30 transition-colors"
  >
    <div className="flex items-start justify-between mb-5">
      <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40">
        {label}
      </div>
      <Icon className="w-4 h-4 text-white/30" strokeWidth={1.75} />
    </div>
    <div className="font-mono text-3xl font-medium text-white tracking-tighter">
      {value}
    </div>
    {hint && <div className="text-xs text-white/40 mt-2">{hint}</div>}
  </div>
);

export default function Performance() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/performance/stats");
      setStats(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const handleReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/performance/reset");
      const d = data.deleted || {};
      toast.success(
        `Reset complete · ${d.calls || 0} calls · ${d.appointments || 0} appts · ${d.consent_records || 0} consents · ${d.feedback || 0} feedback`,
      );
      setShowResetModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset performance data");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div data-testid="performance-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">
            Analytics
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-white">
            Ashley Performance
          </h1>
          <p className="text-sm text-white/50 mt-2 max-w-2xl">
            Booking rate, average time to book, best-performing campaign, and the
            objections hurting your pipeline most.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowResetModal(true)}
          data-testid="performance-reset-btn"
          className="flex items-center gap-2 px-3.5 py-2 rounded-md text-xs font-medium text-red-300 bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 hover:border-red-500/40 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
          Reset to zero
        </button>
      </div>

      {showResetModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="performance-reset-modal"
          onClick={() => !resetting && setShowResetModal(false)}
        >
          <div
            className="bg-[#121214] border border-red-500/30 rounded-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={2} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Reset all performance data?</h2>
                <p className="text-sm text-white/55 mt-1.5 leading-relaxed">
                  This will permanently delete <strong className="text-red-300">all calls, appointments, consent records, and caller feedback</strong> from the database. Real campaigns, clients, and personas are preserved.
                </p>
                <p className="text-xs text-white/40 mt-3">This cannot be undone.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-colors disabled:opacity-50"
                data-testid="performance-reset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold text-white bg-red-500/80 hover:bg-red-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="performance-reset-confirm"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                {resetting ? "Resetting…" : "Yes, reset everything"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-white/40 text-sm">Loading…</div>}

      {!loading && stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi
              testid="kpi-booking-rate"
              icon={Target}
              label="Booking Rate / 100 dials"
              value={`${stats.booking_rate_per_100.toFixed(1)}`}
              hint={`${stats.booked} booked out of ${stats.total_dials}`}
            />
            <Kpi
              testid="kpi-positive-rate"
              icon={TrendingUp}
              label="Positive Rate / 100"
              value={`${stats.positive_rate_per_100.toFixed(1)}`}
              hint={`${stats.booked} booked · ${stats.got_email} email · ${stats.hot_leads} hot`}
            />
            <Kpi
              testid="kpi-avg-time-to-book"
              icon={Timer}
              label="Avg Time to Book"
              value={formatMinutes(stats.average_time_to_book_minutes)}
              hint="From dial start to calendar confirmation"
            />
            <Kpi
              testid="kpi-needs-disposition"
              icon={MessageSquareWarning}
              label="Needs Disposition"
              value={stats.needs_disposition}
              hint="Ended calls with no outcome set"
            />
          </div>

          {stats.best_campaign && (
            <div className="bg-[#121214] border border-emerald-500/20 rounded-xl p-5 mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-emerald-400" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/70">
                  Best Performing Campaign
                </div>
              </div>
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xl font-medium text-white">
                    {stats.best_campaign.campaign_name}
                  </div>
                  <div className="text-xs font-mono text-white/50 mt-1">
                    {stats.best_campaign.booked} booked · {stats.best_campaign.total} dialed
                  </div>
                </div>
                <div className="font-mono text-3xl text-emerald-400 tracking-tighter">
                  {stats.best_campaign.booking_rate}%
                </div>
              </div>
            </div>
          )}

          {/* Campaign breakdown */}
          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden mb-8">
            <div className="px-5 py-4 border-b border-white/5 text-sm font-medium text-white">
              Per-Campaign Breakdown
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="campaign-breakdown-table">
                <thead>
                  <tr className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                    <th className="text-left px-5 py-3 font-normal">Campaign</th>
                    <th className="text-right px-5 py-3 font-normal">Dials</th>
                    <th className="text-right px-5 py-3 font-normal">Booked</th>
                    <th className="text-right px-5 py-3 font-normal">Got Email</th>
                    <th className="text-right px-5 py-3 font-normal">Hot Lead</th>
                    <th className="text-right px-5 py-3 font-normal">Booking %</th>
                    <th className="text-right px-5 py-3 font-normal">Positive %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {stats.campaign_breakdown.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-5 py-10 text-center text-white/40 text-sm"
                      >
                        No campaign data yet.
                      </td>
                    </tr>
                  )}
                  {stats.campaign_breakdown.map((c) => (
                    <tr
                      key={c.campaign_id}
                      data-testid={`breakdown-row-${c.campaign_id}`}
                      className="hover:bg-white/[0.02]"
                    >
                      <td className="px-5 py-3 text-white/90">{c.campaign_name}</td>
                      <td className="px-5 py-3 text-right font-mono text-white/70">{c.total}</td>
                      <td className="px-5 py-3 text-right font-mono text-emerald-400">{c.booked}</td>
                      <td className="px-5 py-3 text-right font-mono text-emerald-300/70">{c.got_email}</td>
                      <td className="px-5 py-3 text-right font-mono text-rose-400/80">{c.hot_leads}</td>
                      <td className="px-5 py-3 text-right font-mono text-white">{c.booking_rate}%</td>
                      <td className="px-5 py-3 text-right font-mono text-[#FFB000]">{c.positive_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By-source funnel */}
          {stats.funnel_by_source && stats.funnel_by_source.length > 0 && (
            <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden" data-testid="funnel-by-source">
              <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
                <Funnel className="w-4 h-4 text-[#FFB000]" />
                <div className="text-sm font-medium text-white">Bookings Funnel by Source</div>
                <span className="text-[10px] font-mono text-white/40 ml-auto">where leads are coming from + converting</span>
              </div>
              <div className="p-5 grid gap-4">
                {stats.funnel_by_source.map((src) => {
                  const Icon = SOURCE_ICONS[src.icon] || Globe;
                  const tint = SOURCE_TINTS[src.key] || SOURCE_TINTS.chatbot;
                  const max = src.stages[0]?.value || 1;
                  return (
                    <div
                      key={src.key}
                      data-testid={`funnel-${src.key}`}
                      className={`rounded-xl border ${tint.border} ${tint.bg} p-4`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2.5">
                          <Icon className={`w-4 h-4 ${tint.text}`} strokeWidth={1.75} />
                          <div className="text-sm font-semibold text-white">{src.label}</div>
                        </div>
                        <div className={`text-xs font-mono ${tint.text} font-semibold`}>
                          {src.conversion_pct}% conversion
                        </div>
                      </div>
                      <div className="grid gap-2">
                        {src.stages.map((stage, i) => {
                          const pct = max ? (stage.value / max) * 100 : 0;
                          const dropPct = i > 0 && src.stages[i - 1].value
                            ? Math.max(0, 100 - (stage.value / src.stages[i - 1].value) * 100)
                            : 0;
                          return (
                            <div key={stage.label} className="grid grid-cols-12 items-center gap-3">
                              <div className="col-span-4 text-[12px] text-white/65">{stage.label}</div>
                              <div className="col-span-6 h-5 bg-white/5 rounded-full overflow-hidden relative">
                                <div
                                  className={`h-full ${tint.bar} rounded-full transition-all`}
                                  style={{ width: `${pct}%` }}
                                />
                                {i > 0 && dropPct > 5 && (
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-white/40 font-mono">
                                    -{dropPct.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                              <div className={`col-span-2 text-right font-mono text-sm ${tint.text} tabular-nums`}>
                                {stage.value}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-white/5 text-[11px] text-white/50">
                        {src.no_show_pct != null && <span>No-show: {src.no_show_pct}%</span>}
                        {src.cancelled != null && <span>Cancelled: {src.cancelled}</span>}
                        {src.verification_pct != null && <span>OTP-verified: {src.verification_pct}%</span>}
                        {src.answer_pct != null && <span>Answer rate: {src.answer_pct}%</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top objections */}
          <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
              <MessageSquareWarning className="w-4 h-4 text-red-400" />
              <div className="text-sm font-medium text-white">
                Top Objections from Transcripts
              </div>
              <span className="text-[10px] font-mono text-white/40">
                Not Interested · Asked for DNC · Hung Up · Wrong Business
              </span>
            </div>
            <div data-testid="top-objections-list">
              {stats.top_objections.length === 0 && (
                <div className="px-5 py-10 text-center text-white/40 text-sm">
                  No objection data yet — need negative-disposition transcripts to analyze.
                </div>
              )}
              {stats.top_objections.map((o, idx) => {
                const max = stats.top_objections[0]?.count || 1;
                const pct = (o.count / max) * 100;
                return (
                  <div
                    key={o.phrase}
                    data-testid={`objection-row-${idx}`}
                    className="px-5 py-3 border-t border-white/5 first:border-t-0 flex items-center gap-4"
                  >
                    <div className="w-6 text-xs font-mono text-white/30 text-right">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/90 font-mono">{o.phrase}</div>
                      <div className="mt-1.5 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500/60 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <div className="font-mono text-sm text-white/70 tabular-nums w-10 text-right">
                      {o.count}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
